// Dashboard: overview + exam selection + progress summary. Also the landing
// target after Stripe Checkout — we read ?checkout=success|cancelled to show a
// note. Entitlement is per-exam: each exam card shows whether you're subscribed,
// and the live subscriptions/{uid}.exams map drives it.

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useExam } from '../context/ExamContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getProgress } from '../lib/db';

export default function Dashboard() {
  const { user } = useAuth();
  const { exams, selectedExamId, selectedExam, selectExam, loading } = useExam();
  const { isEntitled } = useSubscription();
  const [progress, setProgress] = useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const checkout = params.get('checkout');

  useEffect(() => {
    if (!user) return;
    void getProgress(user.uid).then(setProgress);
  }, [user]);

  useEffect(() => {
    if (checkout) {
      const t = setTimeout(() => {
        params.delete('checkout');
        params.delete('exam');
        setParams(params, { replace: true });
      }, 6000);
      return () => clearTimeout(t);
    }
  }, [checkout, params, setParams]);

  const examProgress = selectedExamId ? progress?.byExam?.[selectedExamId] : undefined;
  const pct =
    examProgress && examProgress.answered > 0
      ? Math.round((examProgress.correct / examProgress.answered) * 100)
      : null;

  const selectedEntitled = selectedExam ? isEntitled(selectedExam.id) : false;

  return (
    <>
      <div className="page-head">
        <span className="eyebrow">Dashboard</span>
        <h1>Your preparation</h1>
        <p>Pick the exam you're studying for; it shapes everything the workspace generates.</p>
      </div>

      {checkout === 'success' && (
        <div className="ok-note" style={{ marginBottom: '1.25rem' }}>
          Subscription confirmed. Access unlocks as soon as billing syncs (usually seconds).
        </div>
      )}
      {checkout === 'cancelled' && (
        <div className="error-note" style={{ marginBottom: '1.25rem' }}>
          Checkout was cancelled — no charge was made.
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
        <div className="stat">
          <div className="n">{progress?.totalAnswered ?? 0}</div>
          <div className="k">Questions answered</div>
        </div>
        <div className="stat">
          <div className="n">
            {progress?.totalAnswered
              ? Math.round(((progress.totalCorrect ?? 0) / progress.totalAnswered) * 100)
              : 0}
            %
          </div>
          <div className="k">Overall accuracy</div>
        </div>
        <div className="stat">
          <div className="n">
            {selectedExam ? (pct ?? '—') : '—'}
            {selectedExam && pct !== null ? '%' : ''}
          </div>
          <div className="k">
            {selectedExam ? `${selectedExam.name} accuracy` : 'Selected exam accuracy'}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="panel-title">Choose your exam</div>
        {loading ? (
          <div className="muted">Loading exams…</div>
        ) : exams.length === 0 ? (
          <div className="empty">
            No exam profiles yet. An administrator needs to add one in the Admin surface.
          </div>
        ) : (
          <div className="stack">
            {exams.map((ex) => {
              const entitled = isEntitled(ex.id);
              return (
                <button
                  key={ex.id}
                  className={`exam-option${ex.id === selectedExamId ? ' selected' : ''}`}
                  onClick={() => void selectExam(ex.id)}
                >
                  <div style={{ flex: 1 }}>
                    <h3>{ex.name}</h3>
                    <div className="muted" style={{ fontSize: '0.88rem' }}>
                      {ex.description}
                    </div>
                    <div className="q-meta" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                      {ex.contentAreas.slice(0, 4).map((c) => (
                        <span key={c.id} className="badge">
                          {c.label}
                        </span>
                      ))}
                      {ex.contentAreas.length > 4 && (
                        <span className="badge">+{ex.contentAreas.length - 4}</span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`badge ${entitled ? 'badge-active' : 'badge-inactive'}`}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {entitled ? 'Subscribed' : 'Locked'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedExam && (
        <div className="row between wrap" style={{ marginTop: '1.5rem' }}>
          <div className="muted">
            Studying <strong>{selectedExam.name}</strong>
            {!selectedEntitled && ' — subscribe on the Billing page to generate questions.'}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate(selectedEntitled ? '/workspace' : '/billing')}
          >
            {selectedEntitled ? 'Go to workspace →' : 'Subscribe →'}
          </button>
        </div>
      )}
    </>
  );
}
